import { FC } from "react";
import Image from "next/image";
import styles from "./PackageCard.module.css";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import { useWebTranslation } from "translations";
import { Package } from "components/ContentfulRenderer/ContentfulPackages/types";
import classNames from "classnames";
import { formatCurrency } from "utils/currency";
import PackageCardTab from "./PackageCardTab";
import { AmountToPay } from "utils/insurance/getAmountToPay";
import { isOrg } from "utils/site";
import Colors from "ui-kit/colors";
import { VisuallyHidden } from "@reach/visually-hidden";

interface PackageCardProps {
  pack: Package;
  amountToPay?: AmountToPay;
  rounded?: boolean;
  showMarketing?: boolean;
}

const PackageCard: FC<PackageCardProps> = ({
  pack,
  amountToPay,
  rounded,
  showMarketing,
}) => {
  const { t } = useWebTranslation();

  const { title, subTitle, img, color, popular, popularColor } = pack;

  return (
    <FlexCol justifyContent="flex-end">
      {popular && (
        <PackageCardTab text={t("global.most_popular")} color={popularColor} />
      )}
      <FlexRow
        className={classNames(
          styles.container,
          rounded && styles.rounded,
          popular && styles.popular
        )}
        alignItems="flex-start"
        style={{
          backgroundColor: isOrg("swe") ? "transparent" : color,
          borderColor: isOrg("swe") ? Colors.grey100 : "transparent",
          borderWidth: isOrg("swe") ? 1 : 0,
          borderStyle: isOrg("swe") ? "solid" : "none",
          borderBottom: 0,
        }}
      >
        <Image
          src={img}
          alt=""
          className={styles.image}
          width={42}
          height={50}
        />

        <FlexCol className={styles.text} justifyContent="center">
          <FlexRow justifyContent="space-between" alignItems="center">
            <FlexRow
              justifyContent="space-between"
              style={{ flexDirection: "column" }}
            >
              <Typography variant="h4" component="h2">
                {t(title)}
              </Typography>
              {showMarketing && pack.marketing?.fromPrice ? (
                <Typography variant="body2">
                  {t(pack.marketing?.fromPrice)}
                </Typography>
              ) : null}
            </FlexRow>
            {amountToPay ? (
              <Typography variant="body2" style={{ marginInlineStart: 8 }}>
                <VisuallyHidden>
                  {t("global.price") + " " + formatCurrency(amountToPay.new)}
                </VisuallyHidden>

                <strong aria-hidden>
                  {formatCurrency(amountToPay.old ?? amountToPay.new)}
                </strong>
              </Typography>
            ) : null}
          </FlexRow>
          <Gutter size={0.5} />
          <Typography variant="body3" style={{ margin: 0 }}>
            {showMarketing && pack.marketing?.subTitle
              ? t(pack.marketing.subTitle)
              : t(subTitle)}
          </Typography>
        </FlexCol>
      </FlexRow>
    </FlexCol>
  );
};

export default PackageCard;
